// ═══════════════════════════════════════════════════════
// DESIGN TOKEN SYSTEM — LPA Wealth
// Brand-aligned: dark-native, copper accent, 3-tier type
// ═══════════════════════════════════════════════════════

// ── Type Interfaces ──────────────────────────────────
export interface TypographyScale {
  fontSerif: string;
  fontSans: string;
  fontMono: string;
  xs: string;
  sm: string;
  base: string;
  md: string;
  lg: string;
  xl: string;
  "2xl": string;
  "3xl": string;
  regular: number;
  medium: number;
  semibold: number;
  bold: number;
  heavy: number;
  tight: number;
  normal: number;
  relaxed: number;
}

export interface SpacingScale {
  0: string;
  1: string;
  2: string;
  3: string;
  4: string;
  5: string;
  6: string;
  7: string;
  8: string;
  9: string;
  10: string;
}

export interface RadiusScale {
  sm: string;
  md: string;
  lg: string;
  xl: string;
  full: string;
}

export interface ShadowScale {
  sm: string;
  md: string;
  lg: string;
  glow: string;
}

export interface GlassEffect {
  bg: string;
  border: string;
  blur: string;
  shadow: string;
}

export interface GlassScale {
  dark: GlassEffect;
  light: GlassEffect;
}

export interface GradientScale {
  copper: string;
  copperHover: string;
  hero: string;
}

export interface TransitionScale {
  fast: string;
  normal: string;
  slow: string;
  spring: string;
}

export interface BreakpointScale {
  mobile: number;
  tablet: number;
  desktop: number;
}

export interface ZIndexScale {
  base: number;
  card: number;
  sidebar: number;
  overlay: number;
  modal: number;
  toast: number;
}

export interface ThemeObject {
  navy: string;
  navy2: string;
  gold: string;
  bg: string;
  card: string;
  text: string;
  sub: string;
  border: string;
  muted: string;
  row1: string;
  row2: string;
  accent: string;
  accentHover: string;
  accentText: string;
  surface: string;
  overlay: string;
  success: string;
  danger: string;
  warning: string;
  info: string;
  successBg: string;
  dangerBg: string;
  warningBg: string;
  infoBg: string;
  focusRing: string;
}

// ── Palette ────────────────────────────────────────────
const palette = {
  midnight: "#0F172A",
  obsidian: "#181818",
  charcoal: "#1E293B",
  charcoal2: "#162032",
  copper: "#B87333",
  amber: "#B56A3A",
  platinum: "#F6F3EE",
  steel: "#B0B8C4",
  offWhite: "#F1F5F9",
  mutedSlate: "#9DAAB8",
  borderDark: "#334155",
  navy: "#0B2447",
  gold: "#C9A227",
  success: "#16A34A",
  danger: "#DC2626",
  warning: "#D97706",
  info: "#2563EB",
} as const;

// ── Typography Scale ──────────────────────────────────
export const TYPE: TypographyScale = {
  fontSerif: "'Playfair Display', 'Georgia', serif",
  fontSans: "'Inter', 'Work Sans', system-ui, -apple-system, sans-serif",
  fontMono: "'JetBrains Mono', 'SF Mono', 'Consolas', monospace",
  xs: "0.6875rem",
  sm: "0.75rem",
  base: "0.8125rem",
  md: "0.875rem",
  lg: "1rem",
  xl: "1.25rem",
  "2xl": "1.5rem",
  "3xl": "2rem",
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  heavy: 800,
  tight: 1.2,
  normal: 1.5,
  relaxed: 1.65,
};

// ── Spacing Scale (4px base) ──────────────────────────
export const SP: SpacingScale = {
  0: "0",
  1: "4px",
  2: "8px",
  3: "12px",
  4: "16px",
  5: "20px",
  6: "24px",
  7: "32px",
  8: "40px",
  9: "48px",
  10: "64px",
};

// ── Border Radius ─────────────────────────────────────
export const RADIUS: RadiusScale = {
  sm: "6px",
  md: "10px",
  lg: "14px",
  xl: "20px",
  full: "9999px",
};

// ── Shadows ───────────────────────────────────────────
export const SHADOW: ShadowScale = {
  sm: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)",
  md: "0 2px 8px rgba(0,0,0,0.16), 0 4px 16px rgba(0,0,0,0.08)",
  lg: "0 4px 16px rgba(0,0,0,0.22), 0 8px 32px rgba(0,0,0,0.12)",
  glow: `0 0 20px ${palette.copper}30`,
};

// ── Glassmorphism ─────────────────────────────────────
export const GLASS: GlassScale = {
  dark: {
    bg: "rgba(255,255,255,0.07)",
    border: "rgba(255,255,255,0.10)",
    blur: "blur(16px)",
    shadow: "0 4px 24px rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.15)",
  },
  light: {
    bg: "rgba(255,255,255,0.65)",
    border: "rgba(0,0,0,0.06)",
    blur: "blur(12px)",
    shadow: "0 2px 12px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  },
};

// ── Gradient Accents ──────────────────────────────────
export const GRADIENT: GradientScale = {
  copper: `linear-gradient(135deg, ${palette.copper}, #D4956B)`,
  copperHover: `linear-gradient(135deg, ${palette.amber}, ${palette.copper})`,
  hero: `linear-gradient(135deg, ${palette.midnight} 0%, #162032 50%, ${palette.charcoal} 100%)`,
};

// ── Transitions ───────────────────────────────────────
export const TRANSITION: TransitionScale = {
  fast: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
  normal: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  slow: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  spring: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
};

// ── Breakpoints ───────────────────────────────────────
export const BP: BreakpointScale = {
  mobile: 640,
  tablet: 1024,
  desktop: 1280,
};

// ── Z-index scale ─────────────────────────────────────
export const Z: ZIndexScale = {
  base: 1,
  card: 10,
  sidebar: 20,
  overlay: 30,
  modal: 40,
  toast: 50,
};

// ═══════════════════════════════════════════════════════
// THEME OBJECTS — LIGHT / DARK
// Backward-compatible: old token names preserved, new added
// ═══════════════════════════════════════════════════════

export const LIGHT: ThemeObject = {
  navy: palette.navy,
  navy2: "#19376D",
  gold: palette.gold,
  bg: "#EEF2F7",
  card: "#FFFFFF",
  text: "#1E293B",
  sub: "#475569",
  border: "#E2E8F0",
  muted: "#5F6B7A",
  row1: "#FFFFFF",
  row2: "#F8FAFC",
  accent: "#96602B",
  accentHover: "#7A4F23",
  accentText: "#FFFFFF",
  surface: "#F8FAFC",
  overlay: "rgba(0,0,0,0.4)",
  success: "#15803D",
  danger: palette.danger,
  warning: "#B45309",
  info: palette.info,
  successBg: "#DCFCE7",
  dangerBg: "#FEE2E2",
  warningBg: "#FEF3C7",
  infoBg: "#DBEAFE",
  focusRing: "#96602B",
};

export const DARK: ThemeObject = {
  navy: palette.midnight,
  navy2: palette.charcoal,
  gold: palette.copper,
  bg: palette.midnight,
  card: palette.charcoal,
  text: palette.platinum,
  sub: palette.steel,
  border: palette.borderDark,
  muted: palette.mutedSlate,
  row1: palette.charcoal,
  row2: palette.charcoal2,
  accent: "#CC8544",
  accentHover: palette.amber,
  accentText: palette.midnight,
  surface: palette.charcoal2,
  overlay: "rgba(0,0,0,0.6)",
  success: "#4ADE80",
  danger: "#F87171",
  warning: "#FBBF24",
  info: "#60A5FA",
  successBg: "rgba(74,222,128,0.12)",
  dangerBg: "rgba(248,113,113,0.12)",
  warningBg: "rgba(251,191,36,0.12)",
  infoBg: "rgba(96,165,250,0.12)",
  focusRing: palette.copper,
};
